import { Button, Checkbox, Ellipsis, Icon, Infobox } from '@zoom/components';
import { TFunction } from 'i18next';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PerfectScrollbar from 'react-perfect-scrollbar';

import {
  getAssignedCheckboxFilterItemCheckboxTag,
  getCheckboxFilterSearchInputTag,
  getSeparatorItemTag,
  getUnassignedCheckboxFilterItemCheckboxTag,
  getUnselectAllCheckboxFilterItemsTag,
} from './constants';
import { AcdQueue, Queue, QueueChannelType, ShiftTemplate, Skill, Timezone } from '../../graphql/components';
import {
  AcdQueuesResult,
  QueuesResult,
  ShiftTemplatesResult,
  SkillsAllResult,
  SupervisedTeamsResult,
  TeamsAllResult,
  TimezonesUsedInQueuesResult,
} from '../../graphql/types';
import { HistoricalDataInterval } from '../../scenes/DataImport/utils';
import { SortOrder } from '../../types';
import { recordBy } from '../../utils/array';
import { customSort } from '../../utils/sort';
import { formatTimezoneIdToName } from '../../utils/timezoneFormatter';
import { RenderInput } from '../Inputs/RenderInput';

export const queuesToCheckboxOptionsObj = (
  queuesResult: QueuesResult,
  activeQueuesFilterObj: Record<string, CheckboxFilter>,
  resetAssignedState?: boolean
) => {
  const queuesCheckboxOptionsObj: Record<string, CheckboxFilter> = {};

  queuesResult.data?.queues.forEach((queue: Queue) => {
    queuesCheckboxOptionsObj[queue.id] = {
      id: queue.id,
      name: queue.name,
      assigned: resetAssignedState ? false : !!activeQueuesFilterObj[queue.id],
    };
  });

  return queuesCheckboxOptionsObj;
};

export const channelTypeToCheckboxOptions = (
  t: TFunction,
  activeChannelTypesFilterObj: Record<string, CheckboxFilter>,
  resetAssignedState?: boolean
) => {
  const channelTypeCheckboxOptionsObj: Record<string, CheckboxFilter> = {};

  Object.values(QueueChannelType).forEach((type) => {
    channelTypeCheckboxOptionsObj[type] = {
      id: type,
      name: t(`queue.channelType.${type}`),
      assigned: resetAssignedState ? false : !!activeChannelTypesFilterObj[type],
    };
  });

  return channelTypeCheckboxOptionsObj;
};

export const historicalDataIntervalToCheckboxOptions = (
  t: TFunction,
  activeHistoricalDataIntervalFilterObj: Record<string, CheckboxFilter>,
  resetAssignedState?: boolean
) => {
  const historicalDataIntervalCheckboxOptionsObj: Record<string, CheckboxFilter> = {};

  Object.values(HistoricalDataInterval).forEach((interval) => {
    if (typeof interval === 'number') {
      historicalDataIntervalCheckboxOptionsObj[interval] = {
        id: `${interval}`,
        name: t(`dataImport.historicalDataInterval.${interval}`),
        assigned: resetAssignedState ? false : !!activeHistoricalDataIntervalFilterObj[interval],
      };
    }
  });

  return historicalDataIntervalCheckboxOptionsObj;
};

export const acdToCheckboxOptionsObj = (
  t: TFunction,
  acdQueuesResult: AcdQueuesResult,
  activeAcdQueuesFilterObj: Record<string, CheckboxFilter>,
  resetAssignedState?: boolean
) => {
  const acdCheckboxOptionsObj: Record<string, CheckboxFilter> = {};

  acdQueuesResult.data?.acdQueues.forEach((acdQueue: AcdQueue) => {
    const isDuplicateName = acdQueuesResult.data?.acdQueues.find(
      (option) => option.queueName === acdQueue.queueName && option.id !== acdQueue.id
    );

    acdCheckboxOptionsObj[acdQueue.id] = {
      id: acdQueue.id,
      name: acdQueue.queueName,
      assigned: resetAssignedState ? false : !!activeAcdQueuesFilterObj[acdQueue.id],
      subtitleValue: isDuplicateName ? acdQueue.acdSourceQueueId : undefined,
      subtitleLabel: isDuplicateName ? t('queue.queueId') : undefined,
    };
  });

  return acdCheckboxOptionsObj;
};

export const skillsToCheckboxOptionsObj = (
  skillsResult: SkillsAllResult,
  activeSkillsFilterObj: Record<string, CheckboxFilter>,
  resetAssignedState?: boolean
) => {
  const skillsCheckboxOptionsObj: Record<string, CheckboxFilter> = {};

  skillsResult.data?.skills.forEach((skill: Skill) => {
    skillsCheckboxOptionsObj[skill.id] = {
      id: skill.id,
      name: skill.name,
      assigned: resetAssignedState ? false : !!activeSkillsFilterObj[skill.id],
    };
  });

  return skillsCheckboxOptionsObj;
};

export const shiftTemplatesToCheckboxOptionsObj = (
  shiftTemplatesResult: ShiftTemplatesResult,
  activeShiftTemplatesFilterObj: Record<string, CheckboxFilter>,
  resetAssignedState?: boolean
) => {
  const shiftTemplatesCheckboxOptionsObj: Record<string, CheckboxFilter> = {};

  shiftTemplatesResult.data?.shiftTemplates?.shiftTemplates?.forEach((shiftTemplate: ShiftTemplate) => {
    shiftTemplatesCheckboxOptionsObj[shiftTemplate.id] = {
      id: shiftTemplate.id,
      name: shiftTemplate.name,
      assigned: resetAssignedState ? false : !!activeShiftTemplatesFilterObj[shiftTemplate.id],
    };
  });

  return shiftTemplatesCheckboxOptionsObj;
};

export const teamsToCheckboxOptionsObj = (
  teamsResult: TeamsAllResult,
  activeTeamsFilterObj?: Record<string, CheckboxFilter>,
  resetAssignedState?: boolean
): Record<string, CheckboxFilter> => {
  if (!teamsResult.data?.teams.length) {
    return {};
  }

  return teamsResult.data.teams.reduce((result, { id, name }) => {
    // eslint-disable-next-line no-param-reassign
    result[id] = {
      id,
      name,
      assigned: activeTeamsFilterObj ? (resetAssignedState ? false : !!activeTeamsFilterObj[id]) : false,
    };

    return result;
  }, {} as Record<string, CheckboxFilter>);
};

export const supervisedTeamsToCheckboxOptionsObj = (
  teamsResult: SupervisedTeamsResult,
  activeTeamsFilterObj?: Record<string, CheckboxFilter>,
  resetAssignedState?: boolean
): Record<string, CheckboxFilter> => {
  if (!teamsResult.data?.supervisedTeams.length) {
    return {};
  }

  return teamsResult.data.supervisedTeams.reduce((result, { id, name }) => {
    // eslint-disable-next-line no-param-reassign
    result[id] = {
      id,
      name,
      assigned: activeTeamsFilterObj ? (resetAssignedState ? false : !!activeTeamsFilterObj[id]) : false,
    };

    return result;
  }, {} as Record<string, CheckboxFilter>);
};

export const usedTimezonesToCheckboxOptionsObj = (
  timezoneUsedInQueuesResult: TimezonesUsedInQueuesResult,
  activeTimezonesFilterObj: Record<string, CheckboxFilter>,
  resetAssignedState?: boolean
) => {
  const timezonesCheckboxOptionsObj: Record<string, CheckboxFilter> = {};

  timezoneUsedInQueuesResult.data?.queuesTimezones.forEach(({ zoneId }: Timezone) => {
    timezonesCheckboxOptionsObj[zoneId] = {
      id: zoneId,
      name: formatTimezoneIdToName(zoneId),
      assigned: resetAssignedState ? false : !!activeTimezonesFilterObj[zoneId],
    };
  });

  return timezonesCheckboxOptionsObj;
};

// Use in Accordion Header Tile with CheckboxFilterTile as component
export const createCheckboxFilterTileSubtitle = (
  t: TFunction,
  itemsObj: Record<string, CheckboxFilter>,
  assignedItemIds: string[]
) => {
  const assignedItemsLength = assignedItemIds.length;

  if (assignedItemsLength === 0) {
    return undefined;
  }

  const assignedItem = itemsObj[assignedItemIds[0]];
  let subtitle = assignedItem.name;

  if (assignedItemsLength === 1 && assignedItem.subtitleValue) {
    subtitle = `${subtitle} (${assignedItem.subtitleValue})`;
  }

  return assignedItemsLength === 1 ? subtitle : t('filter.selected', { count: assignedItemsLength });
};

export type CheckboxFilter = {
  assigned: boolean;
  id: string;
  name: string;
  subtitleLabel?: string;
  subtitleValue?: string;
};

export type CheckboxFilterAccordionType = {
  className?: string;
  dataTestIdPrefix?: string;
  id: string;
  sortByField?: keyof CheckboxFilter;
  items: Record<string, CheckboxFilter>;
  multiple?: boolean;
  name: string;
  setItems: (value: React.SetStateAction<Record<string, CheckboxFilter>>) => void;
  subtitle?: string;
};

interface CheckboxFilterTileProps {
  filter: CheckboxFilterAccordionType;
}

const filterBySearchTerm = (term: string, all: Record<string, CheckboxFilter>) => (id: string) =>
  String(all[id].name).toLowerCase().includes(term.trim().toLowerCase()) ||
  String(all[id].subtitleValue).toLowerCase().includes(term.trim().toLowerCase());

export const CheckboxFilterTile: React.FC<CheckboxFilterTileProps> = ({
  filter: { name: filterName, items, multiple = true, setItems, dataTestIdPrefix, sortByField = 'name' },
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');

  // Extracting assigned and unassigned Ids[]
  const { assignedIds, unAssignedIds } = Object.keys(items).reduce(
    (data, itemId) => {
      if (items[itemId].assigned) {
        data.assignedIds.push(itemId);
      } else {
        data.unAssignedIds.push(itemId);
      }
      return data;
    },
    { assignedIds: [] as string[], unAssignedIds: [] as string[] }
  );

  // if sortByField === id then sorting is already done during creating assignedIds and unAssignedIds Objects
  const assignedIdsSorted =
    sortByField !== 'id' ? customSort(items, [sortByField], SortOrder.ASCENDING, assignedIds) : assignedIds;
  const unAssignedIdsSorted =
    sortByField !== 'id' ? customSort(items, [sortByField], SortOrder.ASCENDING, unAssignedIds) : unAssignedIds;

  // Filtration by text search input
  const filteredUnAssignedIdsSorted = unAssignedIdsSorted.filter(filterBySearchTerm(searchTerm, items));

  const toggleCheckedState = (id: string) => {
    // TODO: remove when single select component is implemented
    if (multiple) {
      setItems((previouslySelectedItems) => ({
        ...previouslySelectedItems,
        [id]: {
          ...previouslySelectedItems[id],
          assigned: !previouslySelectedItems[id].assigned,
        },
      }));
    } else {
      setItems((previouslySelectedItems) => {
        const resetItems = recordBy(
          Object.values(previouslySelectedItems).map((item) => ({
            ...item,
            assigned: false,
          })),
          'id'
        );

        return {
          ...resetItems,
          [id]: {
            ...previouslySelectedItems[id],
            assigned: !previouslySelectedItems[id].assigned,
          },
        };
      });
    }
  };

  const unAssignAll = () => assignedIds.map((id) => toggleCheckedState(id));

  const renderCheckboxLabel = (name: string, subtitleLabel?: string, subtitleValue?: string) => {
    return (
      <div className="checkbox-filter__label-wrapper">
        {/* @ts-expect-error: Incompatible type of FC from React < v18 */}
        <Ellipsis>{name}</Ellipsis>
        {subtitleValue && (
          // @ts-expect-error: Incompatible type of FC from React < v18
          <Ellipsis className="checkbox-filter__label-subtitle">
            {subtitleLabel}: <span className="font-weight-bold">{subtitleValue}</span>
          </Ellipsis>
        )}
      </div>
    );
  };

  return (
    <div className="checkbox-filter">
      <div className="checkbox-filter__search-input">
        <RenderInput
          dataTestId={getCheckboxFilterSearchInputTag(filterName, dataTestIdPrefix)}
          meta={{}}
          input={{
            name: 'filter-search',
            onChange: setSearchTerm,
            onBlur: () => ({}),
            onFocus: () => ({}),
            value: searchTerm,
            label: t('filter.search'),
          }}
        />
      </div>
      {filteredUnAssignedIdsSorted.length === 0 && searchTerm && <Infobox>{t('filter.noResults')}</Infobox>}
      <PerfectScrollbar
        className="checkbox-filter__list-wrapper checkbox-filter__list-wrapper--unassigned"
        options={{ suppressScrollX: true }}
      >
        {filteredUnAssignedIdsSorted.map((unAssignedId) => {
          const { id, name, assigned, subtitleLabel, subtitleValue } = items[unAssignedId];
          return (
            <Checkbox
              key={id}
              data-test-id={getUnassignedCheckboxFilterItemCheckboxTag(filterName, id, dataTestIdPrefix)}
              input={{ name, value: assigned }}
              onClick={() => toggleCheckedState(id)}
            >
              {renderCheckboxLabel(name, subtitleLabel, subtitleValue)}
            </Checkbox>
          );
        })}
      </PerfectScrollbar>
      {assignedIds.length > 0 && unAssignedIds.length > 0 && (
        <div className="checkbox-filter__list-separator" data-test-id={getSeparatorItemTag(dataTestIdPrefix)} />
      )}
      <PerfectScrollbar
        className="checkbox-filter__list-wrapper checkbox-filter__list-wrapper--assigned"
        options={{ suppressScrollX: true }}
      >
        {assignedIdsSorted.map((assignedId) => {
          const { id, name, assigned, subtitleLabel, subtitleValue } = items[assignedId];
          return (
            <Checkbox
              key={id}
              data-test-id={getAssignedCheckboxFilterItemCheckboxTag(filterName, id, dataTestIdPrefix)}
              input={{ name, value: assigned }}
              onClick={() => toggleCheckedState(id)}
            >
              {renderCheckboxLabel(name, subtitleLabel, subtitleValue)}
            </Checkbox>
          );
        })}
      </PerfectScrollbar>
      {assignedIds.length > 0 && (
        <div className="checkbox-filter__bulk-reset">
          <Button
            data-test-id={getUnselectAllCheckboxFilterItemsTag(filterName, dataTestIdPrefix)}
            onClick={unAssignAll}
            kind="icon"
            className="p-2"
          >
            <Icon kind="cross" size={15} color="grey3" className="mr-2" />
            {t('filter.reset')}
          </Button>
        </div>
      )}
    </div>
  );
};
